import { MatterActivityFeedActions } from '@/matter/activity-feed/store/slice'
import { ColumnFiltersSchema } from '../schemas'
import { useTableDeletion } from '../hooks/useTableDeletion'
import { Tooltip, useClickInsteadOfHover } from '@/components/ui/tooltip/Tooltip'
import { remainingCapacity, additionLimitMessage } from '../capacity'
import { useTableSearch } from '../hooks/useTableSearch'
import { sourceColumns, defaultSourceQuery } from '../sources/columns'
import { SourceFiltersSchema, SourceSortsSchema } from '../sources/schemas'
import AddFiles from '@/assets/icons/table-add-files.svg?react'
import TableColumns from './TableColumns'
import TableSearch from './TableSearch'
import { useTableLayout } from '../hooks/useTableLayout'
import { defaultTableLayout, visibleColumnOrder, type TableLayout } from '../layout'
import TableActions from './TableActions'
import AddColumnControl from './AddColumnControl'
import DownloadTable from '../export/DownloadTable'
import Breadcrumbs from '@/components/breadcrumbs/Breadcrumbs'
import { selectMatterById } from '@/matter/store/selectors'
import { ArrowLeft, ChevronLeft, ChevronRight } from '@/assets/icons'
import FloatingAssistantButton from '@/components/ai-assistant-button/FloatingAssistantButton'
import { useIsSmallChatBreakpoint } from '@/hooks/useIsSmallChatBreakpoint'
import { IconButton } from '@/components/icon-button/IconButton'
import { AddFilesModal } from '@/chat-common/components/source-dialogs/AddFilesModal'
import { AnalysisActions } from '../analysis/store/slice'
import { useAddTableDocuments } from '../hooks/useAddTableDocuments'
import AnalysisProgress from '../analysis/AnalysisProgress'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@/store/store'
import { useContext, useEffect, useId, useState } from 'react'
import { AuthContext } from '@/context/auth-context'
import type { TableNavigationState } from '../hooks/useChatTable'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@/components/button/Button'
import { matterPaths } from '@/routing/paths'
import { ColumnKind, FilterOperator, FilterMatch, TableQuery } from '../schemas'
import TableOriginTag from './TableOriginTag'
import SourceFilesTab from '../sources/SourceFilesTab'
import { TableErrorRecovery, useTablePage } from '../hooks/useTablePage'
import { useTableDocumentPicker } from '../hooks/useTableDocumentPicker'
import TableGrid from './TableGrid'
import { tableStyles } from './table-styles'
import TableViewTabs from './TableViewTabs'
import { useTableCellDrafts } from '../hooks/useTableCellDrafts'
import TableDocumentUploads from './TableDocumentUploads'
import TableFilters from './TableFilters'
import TableSort from './TableSort'
import DeleteTableItemDialog from './DeleteTableItemDialog'
import { LoadingSkeleton } from '@/components/LoadingSkeleton/LoadingSkeleton'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import {
  trackTableAssistantOpened,
  trackTableColumnHidden,
  trackTableColumnsClicked,
  trackTableFiltersApplied,
  trackTableSortClicked,
} from '@/analytics/utils/table-analytics'

/** A complete saved table, independently of document ingestion or AI execution. */
export default function TableWorkspace({
  matterId,
  tableId,
  refreshKey = '',
  onDeleted,
  onBackToChat,
  onOpenAssistant,
  chatOpen = false,
}: {
  matterId: string
  tableId: string
  refreshKey?: string
  onDeleted?: () => void
  onBackToChat?: () => void
  onOpenAssistant?: () => void
  chatOpen?: boolean
}) {
  const { trackEvent } = useAnalytics()
  const fillAvailableHeight = chatOpen || Boolean(onOpenAssistant)
  const filledPanelClass = `${tableStyles.filledPanel} ${chatOpen ? '!border-l-0' : ''}`
  const isSmallScreen = useIsSmallChatBreakpoint()
  const touchInput = useClickInsteadOfHover()
  const tabsId = useId()
  const layout = useTableLayout(tableId)
  const cellDrafts = useTableCellDrafts()
  const navigate = useNavigate()
  const { initialTable } = (useLocation().state ?? {}) as TableNavigationState
  const dispatch = useDispatch()
  const matter = useSelector((state: RootState) => selectMatterById(state, { matterId }))
  const analysisItems = useSelector((state: RootState) => (state.tableAnalysis?.tableId === tableId ? state.tableAnalysis.run?.sources : undefined))
  const analysisCostLimitReached = useSelector(
    (state: RootState) => state.tableAnalysis?.tableId === tableId && state.tableAnalysis.run?.cost_limit_reached === true
  )
  const [query, setQuery] = useState<TableQuery>({ filters: [], sorts: [], offset: 0, limit: 100 })
  const [addedRowRequest, setAddedRowRequest] = useState(0)
  const { data, requestQuery, displayedQuery, error, errorRecovery, notice, loading, saving, edit, reload, clearError, clearNotice, refreshVersion } =
    useTablePage(tableId, query, refreshKey, layout.layout.hidden, setQuery)
  // Listing metadata only fills the first loading view; fetched data remains authoritative.
  const loadingTable = loading && !data && !error && initialTable?.id === tableId && initialTable.matter_id === matterId ? initialTable : undefined
  const displayedTable = data?.table ?? loadingTable
  const documentBased = data?.document_based ?? loadingTable?.document_analysis
  const showPagination = displayedQuery.offset > 0 || (data?.has_more ?? (data?.total == null || data.total > displayedQuery.limit))
  // Reconciled metadata includes manual edits, source changes, and published analysis.
  // Query and layout changes keep this timestamp stable.
  const activityTime = data?.table.updated_at ?? data?.table.created_at
  useEffect(() => {
    if (activityTime !== undefined) dispatch(MatterActivityFeedActions.invalidateMatterActivity({ matterId }))
  }, [activityTime, matterId, dispatch])
  const tableChanged = () => {
    dispatch(MatterActivityFeedActions.invalidateMatterActivity({ matterId }))
    reload()
  }
  const [searchPending, setSearchPending] = useState(false)
  const searchNavigation = useTableSearch({
    tableId,
    data,
    query: requestQuery,
    displayedQuery,
    loading,
    error,
    layout: layout.layout,
    refreshVersion,
    onQueryChange: setQuery,
  })
  const { user } = useContext(AuthContext)
  const canEdit = Boolean(user?.uid && data?.table.id === tableId && data.table.created_by_user_id === user.uid)
  const documentPicker = useTableDocumentPicker(tableId)
  const [sourceQuery, setSourceQuery] = useState(defaultSourceQuery)
  const [sourceLayout, setSourceLayout] = useState(defaultTableLayout)
  const changeLayout = (update: (current: TableLayout) => TableLayout) => {
    const next = update(layout.layout)
    if (query.search?.trim() && next.hidden !== layout.layout.hidden) setQuery((current) => ({ ...current, offset: 0 }))
    layout.change(() => next)
  }
  const changeSourceLayout = (update: (current: TableLayout) => TableLayout) => {
    const next = update(sourceLayout)
    if (sourceQuery.search?.trim() && next.hidden !== sourceLayout.hidden) setSourceQuery((current) => ({ ...current, offset: 0 }))
    setSourceLayout(next)
  }
  const viewSourceStatuses = (statuses: string) => {
    setSourceQuery((current) => ({
      ...current,
      filters: [{ column_id: 'status', operator: FilterOperator.Equals, value: statuses }],
      search: '',
      status: undefined,
      filter_match: FilterMatch.All,
      offset: 0,
    }))
    setSourceLayout((current) => ({ ...current, hidden: current.hidden.filter((id) => id !== 'status' && id !== 'document_name') }))
    setActiveTab('sources')
  }
  const [activeTab, setActiveTab] = useState<'table' | 'sources'>('table')
  const {
    addingDocuments,
    addDocumentsError,
    addDocuments,
    clearError: clearAddDocumentsError,
  } = useAddTableDocuments(
    matterId,
    tableId,
    () => {
      setQuery((current) => ({ ...current, search: '', filters: [], sorts: [], offset: 0 }))
    },
    reload
  )
  const analysisQuestionKey = JSON.stringify(data?.columns.map(({ id, question, manual_entry }) => [id, question, manual_entry]))
  const visibleRowKey = data?.rows.map((row) => row.id).join(',') ?? ''
  useEffect(() => {
    if (data?.document_based) dispatch(AnalysisActions.visibleRows({ tableId, rowIds: visibleRowKey ? visibleRowKey.split(',') : [] }))
  }, [dispatch, tableId, visibleRowKey, data?.document_based, analysisQuestionKey])
  const { deletion, setDeletion, deleting, deleteError, confirmDelete } = useTableDeletion({
    edit,
    discardDeleted: cellDrafts.discardDeleted,
    setQuery,
    reload,
  })
  const sort = (columnId: string, descending?: boolean) => {
    clearError()
    setQuery((current) => {
      const existing = current.sorts.length === 1 ? current.sorts.find((item) => item.column_id === columnId) : undefined
      return {
        ...current,
        offset: 0,
        sorts:
          descending !== undefined
            ? [{ column_id: columnId, descending }]
            : existing?.descending
            ? []
            : [{ column_id: columnId, descending: Boolean(existing) }],
      }
    })
  }
  if (data && data.table.matter_id !== matterId) {
    return (
      <p className="p-6" role="alert">
        This table does not belong to this matter.
      </p>
    )
  }
  return (
    <div
      className={`relative flex flex-col gap-4 min-w-0 ${chatOpen ? '' : 'px-4'} ${fillAvailableHeight ? 'min-h-0 flex-1' : !onBackToChat ? 'pb-20' : 'pb-4'}`}
    >
      <div className={`-mb-4 shrink-0 border-b ${chatOpen ? 'border-brand-neutral-300' : 'border-brand-neutral-200'}`}>
        <div className={`flex ${chatOpen ? 'h-[49px]' : 'h-[59px]'} min-w-0 items-center px-3`}>
          {onBackToChat && (
            <>
              <IconButton
                variant="tonal"
                className="shrink-0"
                size="sm"
                icon={<ArrowLeft />}
                aria-label="Back to chat"
                tooltipText="Back to chat"
                onClick={onBackToChat}
              />
              <div className="ml-5 mr-[14px] h-7 w-px shrink-0 bg-brand-neutral-300" />
            </>
          )}
          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden pr-2">
            <div className="min-w-0 overflow-hidden">
              <Breadcrumbs
                items={[
                  { id: matterId, label: matter?.name ?? 'Matter', onClick: () => navigate(matterPaths.overview(matterId)) },
                  { id: 'tables', label: 'Tables', onClick: () => navigate(matterPaths.tables(matterId)) },
                  { id: tableId, label: displayedTable?.name ?? 'Table' },
                ]}
                maxLabelLength={30}
                persistFirstItem
              />
            </div>
            <TableOriginTag documentAnalysis={displayedTable?.document_analysis ?? false} />
          </div>
          <div className="ml-auto flex items-center gap-2">
            {(data || loading) && (
              <DownloadTable
                rowCount={searchNavigation.totalRows}
                totalRowCount={data?.capacity?.row_count ?? (!requestQuery.filters.length && !requestQuery.search?.trim() ? data?.total : null)}
                tableId={tableId}
                query={requestQuery}
                disabled={!data || displayedQuery !== requestQuery || !layout.loaded}
                documentBased={documentBased}
                pinnedColumnIds={layout.layout.pinned.filter((id) => !layout.layout.hidden.includes(id))}
                columnIds={visibleColumnOrder([...(documentBased ? ['row'] : []), ...(data?.columns ?? []).map((column) => column.id)], layout.layout)}
              />
            )}
            {canEdit && data && (
              <TableActions
                tableId={tableId}
                name={data.table.name}
                disabled={saving || deleting}
                onChanged={tableChanged}
                onDeleted={onDeleted ?? (() => navigate(matterPaths.tables(matterId)))}
              />
            )}
          </div>
        </div>
      </div>
      {data?.document_based && (
        <div className={`-mb-3 flex shrink-0 flex-wrap items-center gap-2 pt-3 empty:hidden ${chatOpen ? 'pl-4 pr-5' : ''}`}>
          {data?.document_based && (
            <AnalysisProgress
              tableId={tableId}
              canEdit={canEdit}
              onProgress={reload}
              onViewErrors={() => viewSourceStatuses('Failed')}
              onViewPaused={() => viewSourceStatuses('Awaiting analysis,Cancelled')}
              pendingQuestions={data?.columns.filter((column) => column.pending_analysis).length ?? 0}
              analysisQuestionCount={data?.columns.filter((column) => !column.manual_entry && column.question?.trim()).length ?? 0}
              documentCount={data?.document_count ?? 0}
            />
          )}
        </div>
      )}
      <div
        role="group"
        aria-label="Table views"
        aria-busy={loading && !data}
        className="flex shrink-0 flex-wrap-reverse items-start justify-between gap-x-4 border-b border-brand-neutral-200 [container-type:inline-size]"
      >
        {documentBased !== undefined && <TableViewTabs id={tabsId} value={activeTab} onChange={setActiveTab} documentBased={documentBased} disabled={!data} />}
        {loading && documentBased === undefined && (
          <div aria-hidden="true" className="flex h-7 items-center self-start rounded-t-md border border-brand-neutral-200 bg-brand-neutral-50 px-3">
            <LoadingSkeleton width={36} lineHeight={16} />
          </div>
        )}
        <div className={`ml-auto flex w-full max-w-full items-center gap-3 pr-5 [@container_(min-width:_640px)]:w-auto ${chatOpen ? 'py-2' : 'pt-2 pb-[7px]'}`}>
          <div
            hidden={activeTab === 'sources' && data?.document_based}
            className={activeTab === 'sources' && data?.document_based ? 'hidden' : 'flex min-h-7 min-w-0 items-center gap-1'}
          >
            {(data || loading) && (
              <TableFilters
                disabled={!data}
                columns={data?.columns ?? []}
                filters={query.filters}
                filterMatch={query.filter_match}
                onChange={(filters, filter_match) => {
                  trackTableFiltersApplied(trackEvent, tableId, filters.length, filter_match)
                  clearError()
                  setQuery({ ...query, filters: ColumnFiltersSchema.parse(filters), filter_match, offset: 0 })
                }}
              />
            )}
            {(data || loading) && (
              <TableSort
                disabled={!data}
                columns={data?.columns ?? []}
                sorts={query.sorts}
                onOpen={() => trackTableSortClicked(trackEvent, tableId)}
                onChange={(sorts) => {
                  clearError()
                  setQuery({ ...query, sorts, offset: 0 })
                }}
              />
            )}
            {(data || loading) && (
              <TableColumns
                columns={
                  documentBased ? [{ id: 'row', name: 'File', kind: ColumnKind.Text, source_file: true }, ...(data?.columns ?? [])] : data?.columns ?? []
                }
                layout={layout.layout}
                disabled={!data || !layout.loaded}
                onOpen={() => trackTableColumnsClicked(trackEvent, tableId)}
                onChange={changeLayout}
              />
            )}
            {(data || loading) && (
              <TableSearch
                value={query.search ?? ''}
                navigation={searchNavigation}
                onPendingChange={setSearchPending}
                disabled={!data || !layout.loaded}
                onChange={(search) => {
                  clearError()
                  setQuery((current) => ({
                    ...current,
                    search,
                    offset: 0,
                  }))
                }}
              />
            )}
          </div>
          {activeTab === 'sources' && (
            <div className="flex min-w-0 items-center gap-1">
              <TableFilters
                columns={sourceColumns}
                filters={sourceQuery.filters}
                filterMatch={sourceQuery.filter_match}
                onChange={(filters, filter_match) => {
                  trackTableFiltersApplied(trackEvent, tableId, filters.length, filter_match)
                  setSourceQuery((current) => ({ ...current, filters: SourceFiltersSchema.parse(filters), filter_match, offset: 0 }))
                }}
              />
              <TableSort
                columns={sourceColumns}
                sorts={sourceQuery.sorts}
                emptyLabel="Source file A–Z."
                onOpen={() => trackTableSortClicked(trackEvent, tableId)}
                onChange={(sorts) => setSourceQuery((current) => ({ ...current, sorts: SourceSortsSchema.parse(sorts), offset: 0 }))}
              />
              <TableColumns
                columns={sourceColumns}
                layout={sourceLayout}
                disabled={false}
                minimumVisible={1}
                onOpen={() => trackTableColumnsClicked(trackEvent, tableId)}
                onChange={changeSourceLayout}
              />
              <TableSearch
                filenamesOnly
                value={sourceQuery.search ?? ''}
                onChange={(search) => setSourceQuery((current) => ({ ...current, search, offset: 0 }))}
              />
            </div>
          )}
          {(canEdit || (loadingTable && user?.uid === loadingTable.created_by_user_id)) && (
            <div className="flex shrink-0 items-center gap-2 border-l border-brand-neutral-200 pl-3">
              {documentBased && (
                <Tooltip
                  title={additionLimitMessage(remainingCapacity(data?.capacity, 'rows'), 1, 'documents') ?? (touchInput ? '' : 'Add Files')}
                  disableInteractive
                >
                  <span tabIndex={remainingCapacity(data?.capacity, 'rows') === 0 ? 0 : undefined}>
                    <Button
                      size="xs"
                      variant="tonal"
                      color="neutral"
                      leadingIcon={<AddFiles className="size-4" />}
                      aria-label="Add Files"
                      className="!gap-1 !px-2 disabled:!ring-0"
                      disabled={!data || addingDocuments || documentPicker.loading || remainingCapacity(data?.capacity, 'rows') === 0}
                      onClick={() => void documentPicker.show()}
                    >
                      <span className="hidden [@container_(min-width:_640px)]:inline">Add Files</span>
                    </Button>
                  </span>
                </Tooltip>
              )}
              {displayedTable && (
                <div className={activeTab === 'sources' ? 'hidden' : undefined}>
                  <AddColumnControl
                    limitMessage={additionLimitMessage(remainingCapacity(data?.capacity, 'columns'), 1, 'columns')}
                    documentBased={Boolean(documentBased)}
                    saving={!data || saving}
                    loading={loading}
                    onSave={edit}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {data?.document_based && (
        <div
          role="tabpanel"
          id={`${tabsId}-sources-panel`}
          aria-labelledby={`${tabsId}-sources-tab`}
          hidden={activeTab !== 'sources'}
          className={activeTab === 'sources' ? `${tableStyles.panel} ${fillAvailableHeight ? filledPanelClass : ''}` : 'hidden'}
        >
          <SourceFilesTab
            fillAvailableHeight={fillAvailableHeight}
            costLimitReached={analysisCostLimitReached}
            key={tableId}
            query={sourceQuery}
            onQueryChange={setSourceQuery}
            hiddenColumns={sourceLayout.hidden}
            tableId={tableId}
            matterId={matterId}
            canEdit={canEdit}
            onRemoved={() => {
              reload()
              dispatch(AnalysisActions.refresh(tableId))
            }}
          />
        </div>
      )}
      {layout.error && (
        <p role="alert" className="text-sm text-red-800">
          {layout.error}{' '}
          <Button size="sm" variant="plain" onClick={layout.retry}>
            Retry layout
          </Button>
        </p>
      )}
      {addDocumentsError && (
        <div role="alert" className="text-sm text-red-800">
          {addDocumentsError}
          <Button
            size="sm"
            variant="plain"
            onClick={() => {
              clearAddDocumentsError()
              void documentPicker.show()
            }}
          >
            Choose documents
          </Button>
        </div>
      )}
      {documentPicker.loading && <p role="status">Loading existing table documents…</p>}
      {documentPicker.error && <p role="alert">{documentPicker.error}</p>}
      {data && !canEdit && (
        <p className={`shrink-0 py-2 text-sm text-brand-neutral-600 ${fillAvailableHeight ? 'px-4' : ''}`}>
          You can view, sort, and filter this table. Only its creator can edit it.
        </p>
      )}
      {notice && (
        <div role="status" className="rounded bg-brand-neutral-100 p-3 text-sm text-brand-neutral-800">
          {notice}{' '}
          <Button size="sm" variant="plain" onClick={clearNotice}>
            Dismiss
          </Button>
        </div>
      )}
      {error && !deletion && (
        <div role="alert" className="rounded bg-red-50 p-3 text-red-800">
          {error}{' '}
          {errorRecovery === TableErrorRecovery.Reload && (
            <Button size="sm" variant="plain" onClick={reload}>
              Reload table
            </Button>
          )}
          {errorRecovery === TableErrorRecovery.ClearView && (
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              onClick={() => {
                clearError()
                setQuery((current) => ({ ...current, filters: [], sorts: [], offset: 0 }))
              }}
            >
              {query.filters.length > 0 ? (query.sorts.length > 0 ? 'Clear filters and sorting' : 'Clear filters') : 'Clear sorting'}
            </Button>
          )}
          {errorRecovery === TableErrorRecovery.BackToTables && (
            <Button size="sm" variant="outlined" color="neutral" onClick={() => navigate(matterPaths.tables(matterId))}>
              Back to Tables
            </Button>
          )}
        </div>
      )}
      {loading && (
        <div role="status" className={data ? 'sr-only' : `${tableStyles.panel} ${fillAvailableHeight ? filledPanelClass + ' !pb-0' : ''}`}>
          <p className="sr-only">Loading table…</p>
          {!data && (
            <div aria-hidden="true" className={`${tableStyles.container} ${fillAvailableHeight ? tableStyles.filledContainer : ''}`}>
              <div className="grid h-10 grid-cols-3 divide-x divide-brand-neutral-200 border-b border-brand-neutral-200 bg-brand-neutral-50">
                {[45, 60, 35].map((width) => (
                  <div key={width} className="flex items-center px-4">
                    <LoadingSkeleton width={`${width}%`} lineHeight={16} />
                  </div>
                ))}
              </div>
              {Array.from({ length: 5 }, (_, row) => (
                <div key={row} className="grid grid-cols-3 divide-x divide-brand-neutral-200 border-b border-brand-neutral-200 last:border-b-0">
                  {[65, 80, 55].map((width) => (
                    <div key={width} className="px-4 py-3">
                      <LoadingSkeleton width={`${width}%`} lineHeight={16} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {data && (activeTab === 'table' || !data.document_based) && (
        <div
          role="tabpanel"
          id={`${tabsId}-table-panel`}
          aria-labelledby={`${tabsId}-table-tab`}
          className={`${tableStyles.panel} ${fillAvailableHeight ? filledPanelClass + ' !pb-0' : ''}`}
        >
          <div className={`contents ${searchPending || (loading && query.search) ? '[&_table]:opacity-50' : ''}`}>
            <TableGrid
              activeMatch={searchPending ? null : searchNavigation.activeMatch}
              pagination={
                showPagination && (
                  <nav aria-label="Table pagination" className={`${tableStyles.pagination} sticky left-0 mt-auto py-4`}>
                    <IconButton
                      icon={<ChevronLeft />}
                      aria-label="Previous"
                      variant="tonal"
                      size="xs"
                      disabled={query.offset === 0 || saving || loading}
                      onClick={() => setQuery({ ...query, offset: Math.max(0, query.offset - query.limit) })}
                    />
                    <span className="text-xs text-brand-neutral-950">
                      Page {Math.floor(displayedQuery.offset / displayedQuery.limit) + 1}
                      {searchNavigation.totalRows != null ? ` of ${Math.max(1, Math.ceil(searchNavigation.totalRows / displayedQuery.limit))}` : ''}
                    </span>
                    <IconButton
                      icon={<ChevronRight />}
                      aria-label="Next"
                      variant="tonal"
                      size="xs"
                      disabled={!(data.has_more ?? (data.total != null && query.offset + query.limit < data.total)) || saving || loading}
                      onClick={() => setQuery({ ...query, offset: query.offset + query.limit })}
                    />
                  </nav>
                )
              }
              fillAvailableHeight={fillAvailableHeight}
              cellDrafts={cellDrafts}
              addedRowRequest={addedRowRequest}
              layout={layout.layout}
              layoutDisabled={!layout.loaded}
              onLayoutChange={changeLayout}
              analysisItems={analysisItems}
              analysisCostLimitReached={analysisCostLimitReached}
              data={data}
              query={displayedQuery}
              canEdit={canEdit}
              saving={deleting || saving || displayedQuery !== requestQuery || addingDocuments || documentPicker.loading}
              addRowLabel={data.document_based ? 'Add documents' : 'Add row'}
              onAddRow={async () => {
                if (data.document_based) {
                  void documentPicker.show()
                  return
                }
                if ((await edit({ action: 'add-row' })).ok) {
                  setQuery({ filters: [], sorts: [], limit: query.limit, offset: 0 })
                  setAddedRowRequest((request) => request + 1)
                }
              }}
              onEdit={edit}
              onSort={sort}
              onDeleteRows={(rowIds) =>
                setDeletion({
                  title: rowIds.length === 1 ? 'Delete row?' : 'Delete ' + rowIds.length + ' rows?',
                  request: { action: 'delete-rows', row_ids: rowIds },
                })
              }
              onDeleteColumn={(columnId) => setDeletion({ title: 'Delete column?', request: { action: 'delete-column', column_id: columnId } })}
              onColumnHidden={() => trackTableColumnHidden(trackEvent, tableId)}
            />
          </div>
        </div>
      )}
      {data && onOpenAssistant && (
        <FloatingAssistantButton
          isSmallScreen={isSmallScreen}
          onClick={() => {
            trackTableAssistantOpened(trackEvent, tableId)
            onOpenAssistant()
          }}
        />
      )}
      {documentPicker.open && (
        <AddFilesModal
          includeProcessingFiles
          open
          title="Add documents"
          uploadPanel={<TableDocumentUploads matterId={matterId} tableId={tableId} remaining={remainingCapacity(data?.capacity, 'rows')} />}
          matterId={matterId}
          onClose={documentPicker.close}
          onFilesSelected={() => undefined}
          submitSelection={addDocuments}
          inlineMessage={addDocumentsError ? { variant: 'error', children: addDocumentsError } : undefined}
          validateSelection={(ids) =>
            additionLimitMessage(remainingCapacity(data?.capacity, 'rows'), new Set(ids.filter((id) => !documentPicker.documentIds.has(id))).size, 'documents')
          }
          isFileSelectable={(file) => !documentPicker.documentIds.has(file.id)}
          fileNotSelectableMessage={(file) => (documentPicker.documentIds.has(file.id) ? 'Already included.' : undefined)}
        />
      )}
      {deletion && (
        <DeleteTableItemDialog
          title={deletion.title}
          subtitle="This removes the selected items and their contents. This cannot be undone."
          error={deleteError || error}
          saving={deleting}
          onCancel={() => setDeletion(null)}
          onConfirm={() => void confirmDelete()}
        />
      )}
    </div>
  )
}
